import { EmptyStateProps, Table } from '@silverfort/sf-components';
import { FullItem, TabConfig, TabValue } from '../../../types';
import { getTableColumns } from '../utils';
import { TableRow } from '../types';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';

const ROW_HEIGHT = 40;
const HEADER_HEIGHT = 40;
const GAP_BETWEEN_HEADER_AND_BODY = 8;

interface SingleDataConfig {
    data: FullItem[];
    loading: boolean;
}

interface MultiTabProps<T extends TabValue = TabValue> {
    selectedTab: T;
    tabs: TabConfig<T>[];
    singleData?: never;
}

interface SingleDataProps {
    selectedTab?: never;
    tabs?: never;
    singleData: SingleDataConfig;
}

type Props<T extends TabValue = TabValue> = (MultiTabProps<T> | SingleDataProps) & { maxHeight: number };

const TopMetricTable = <T extends TabValue>(props: Props<T>) => {
    let rows: TableRow[];
    let isLoading: boolean;
    let emptyState: EmptyStateProps;

    if (props.singleData) {
        rows = props.singleData.data || [];
        isLoading = props.singleData.loading || false;

        emptyState = {
            title: 'No data yet',
            className: '[&&]:h-50',
            description: ''
        };
    } else {
        const { selectedTab, tabs } = props;
        const currentTab = tabs.find((tab) => tab.value === selectedTab);
        rows = (currentTab?.fullData || []).map((row) => ({
            ...row,
            metadata: {
                ...(row.metadata as Record<string, unknown>),
                adType: selectedTab === 'users' ? AD_ENTITY_TYPES.USER : AD_ENTITY_TYPES.SERVER
            }
        }));
        isLoading = currentTab?.fullLoading || false;

        const defaultEmptyState: EmptyStateProps = {
            title: `No ${selectedTab.toString()} yet`,
            className: '[&&]:h-50',
            description: ''
        };

        emptyState = {
            ...defaultEmptyState,
            ...currentTab?.emptyStateConfig,
            description: currentTab?.emptyStateConfig?.description || ''
        };
    }

    const withBadgeInNameCell = rows.some((row) => row.metadata?.isProtected !== undefined);
    const maxBodyHeight = Math.max(props.maxHeight - HEADER_HEIGHT - GAP_BETWEEN_HEADER_AND_BODY, 0);

    return (
        <Table
            tableContainerClassName={`${rows.length ? 'h-full' : 'h-fit'} overflow-hidden [&]:w-[822px]`}
            columns={getTableColumns(withBadgeInNameCell)}
            rows={rows}
            keyExtractor={(row) => row.hits}
            bodyHeight={isLoading ? maxBodyHeight : Math.min(ROW_HEIGHT * rows.length + ROW_HEIGHT, maxBodyHeight)}
            loading={isLoading}
            EmptyStateProps={emptyState}
            headerClassName="[&]:w-[822px]"
        />
    );
};

export default TopMetricTable;
